import React from 'react'
import classNames from 'classnames'
import { Form, Row, Col } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Moment } from 'moment'
import { DatePicker } from 'antd'
import NumberFormat from 'react-number-format'

import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import { prefix as prefixBase } from '../../../constants'
import styles from './FormRequestCourse.module.scss'

const { RangePicker } = DatePicker

export enum Fields {
  name = 'name',
  startDate = 'startDate',
  endDate = 'endDate',
  hours = 'hours',
  sence_code = 'sence_code',
  cost = 'cost',
  link = 'link',
  message = 'message',
}

type ValidationResult = React.ReactNode

export interface RequestNewCourseForm {
  [Fields.name]: string
  [Fields.startDate]?: Moment
  [Fields.endDate]?: Moment
  [Fields.hours]: number | null
  [Fields.sence_code]: string
  [Fields.cost]: string
  [Fields.link]: string
  [Fields.message]: string
}
export type Validation<V> = (value: V) => ValidationResult

export type Validations = {
  [P in keyof RequestNewCourseForm]?: Validation<RequestNewCourseForm[P]>[]
}

type GetValue<F extends keyof RequestNewCourseForm> = RequestNewCourseForm[F]

interface FormRequestCourseProps {
  values: RequestNewCourseForm
  setValues: React.Dispatch<React.SetStateAction<RequestNewCourseForm>>
  validations?: Validations
}

export const FormRequestCourse = ({
  values,
  setValues,
  validations: validationsBase,
}: FormRequestCourseProps) => {
  const { t } = useTranslation()
  const errorsRef = React.useRef<
    Partial<Record<keyof RequestNewCourseForm, ValidationResult>>
  >({})

  const errors = errorsRef.current

  const setFieldValue = <F extends keyof RequestNewCourseForm>(
    fieldName: F,
    value: GetValue<F>
  ) => {
    setFieldValues([[fieldName, value]])
  }

  const setFieldValues = <F extends keyof RequestNewCourseForm>(
    fields: [F, GetValue<F>][]
  ) => {
    const newValues = { ...values }
    for (const [fieldName, value] of fields) {
      errorsRef.current[fieldName] = getError(fieldName, value)
      newValues[fieldName] = value
    }
    setValues({ ...newValues })
  }

  const getValue = <F extends keyof RequestNewCourseForm>(
    fieldName: F
  ): GetValue<F> => values[fieldName]

  const getError = <F extends keyof RequestNewCourseForm>(
    field: F,
    value: GetValue<F>
  ): ValidationResult | undefined => {
    const validations = validationsBase?.[field]
    if (validations)
      for (let index = 0; index < validations.length; index++) {
        const validation = validations[index]
        const error = validation?.(value as never)
        if (error) return error
      }
  }

  const prefix = `${prefixBase}.fields`
  const prefixPlaceHolders = `${prefixBase}.placeHolders`

  const startDate = getValue(Fields.startDate)
  const endDate = getValue(Fields.endDate)

  return (
    <Form>
      <Row className={styles.formGroup}>
        <Col sm="12" className={'pt-3'}>
          <InputLabel label={<>{t(`${prefix}.name`)}*</>} />
          <Form.Control
            name={Fields.name}
            value={getValue(Fields.name)}
            placeholder={t(`${prefixPlaceHolders}.name`)}
            onChange={(e) => {
              setFieldValue(Fields.name, e.target.value)
            }}
            isInvalid={!!errors[Fields.name]}
            className={styles.requestNewCourseInput}
          />
          <Form.Control.Feedback type="invalid">
            {errors[Fields.name]}
          </Form.Control.Feedback>
        </Col>
        <Col sm="6" className={'pt-3'}>
          <InputLabel label={<>{t(`${prefix}.date`)}*</>} />
          <RangePicker
            placeholder={t(`${prefixPlaceHolders}.date`, {
              returnObjects: true,
            })}
            onChange={([startDate, endDate]) => {
              if (startDate && endDate) {
                setFieldValues([
                  [Fields.startDate, startDate],
                  [Fields.endDate, endDate],
                ])
              }
            }}
            pickerInputClass={styles.rangePicker}
            dropdownClassName={styles.rangePicker}
            pickerClass={styles.rangePicker}
            className={styles.rangePicker}
            value={(startDate && endDate && [startDate, endDate]) || undefined}
          />
        </Col>
        <Col sm="6" className={'pt-3'}>
          <InputLabel label={<>{t(`${prefix}.hours`)}*</>} />
          <Form.Control
            name={Fields.hours}
            value={`${getValue(Fields.hours) || ''}`}
            placeholder={t(`${prefixPlaceHolders}.hours`)}
            type="number"
            onChange={(e) => {
              const value = +e.target.value
              setFieldValue(Fields.hours, value > 0 ? value : 0)
            }}
            isInvalid={!!errors[Fields.hours]}
            className={styles.requestNewCourseInput}
          />
          <Form.Control.Feedback type="invalid">
            {errors[Fields.hours]}
          </Form.Control.Feedback>
        </Col>
        <Col sm="6" className={'pt-3'}>
          <InputLabel label={t(`${prefix}.code`)} />
          <Form.Control
            name={Fields.sence_code}
            value={getValue(Fields.sence_code)}
            placeholder={t(`${prefixPlaceHolders}.code`)}
            onChange={(e) => {
              setFieldValue(Fields.sence_code, e.target.value)
            }}
            isInvalid={!!errors[Fields.sence_code]}
            className={styles.requestNewCourseInput}
          />
          <Form.Control.Feedback type="invalid">
            {errors[Fields.sence_code]}
          </Form.Control.Feedback>
        </Col>
        <Col sm="6" className={'pt-3'}>
          <InputLabel label={t(`${prefix}.cost`)} />
          <NumberFormat
            name={Fields.cost}
            value={getValue(Fields.cost)}
            className={classNames(
              styles.requestNewCourseInput,
              'form-control',
              !!errors[Fields.cost] && 'is-invalid'
            )}
            placeholder={t(`${prefixPlaceHolders}.cost`)}
            onValueChange={({ value }) => {
              setFieldValue(Fields.cost, value || '')
            }}
            prefix={'$'}
            thousandSeparator
            isNumericString
          />
          <Form.Control.Feedback type="invalid">
            {errors[Fields.cost]}
          </Form.Control.Feedback>
        </Col>
        <Col sm="12" className={'pt-3'}>
          <InputLabel label={<>{t(`${prefix}.link`)}</>} />
          <Form.Control
            name={Fields.link}
            value={getValue(Fields.link)}
            placeholder={t(`${prefixPlaceHolders}.links`)}
            onChange={(e) => {
              setFieldValue(Fields.link, e.target.value)
            }}
            isInvalid={!!errors[Fields.link]}
            className={styles.requestNewCourseInput}
          />
          <Form.Control.Feedback type="invalid">
            {errors[Fields.link]}
          </Form.Control.Feedback>
        </Col>
        <Col sm="12" className={'pt-3'}>
          <InputLabel label={<>{t(`${prefix}.message`)}*</>} />
          <Form.Control
            as="textarea"
            name={Fields.message}
            value={getValue(Fields.message)}
            placeholder={t(`${prefixPlaceHolders}.message`)}
            onChange={(e) => {
              setFieldValue(Fields.message, e.target.value)
            }}
            className={`${styles.textbox} pb-0`}
            rows={4}
            isInvalid={!!errors[Fields.message]}
          />
          <Form.Control.Feedback type="invalid">
            {errors[Fields.message]}
          </Form.Control.Feedback>
        </Col>
      </Row>
    </Form>
  )
}
